import { ApolloLink } from '@apollo/client';
import { onError } from '@apollo/link-error';
import * as Sentry from '@sentry/react';

import getEnv from './utils/getEnv';

// Minimal structural shape of a GraphQL error, so we avoid importing the
// `graphql` package just for a type.
type GraphQLErrorLike = {
  message: string;
  extensions?: Record<string, unknown> | null;
};

/**
 * GraphQL error codes that represent expected/business outcomes rather than
 * actionable engineering faults. These are the bulk of the production Sentry
 * noise — quote expiry, validation failures, auth challenges, etc. — and should
 * not be reported. Genuine server faults (no code, or 5xx-equivalent codes such
 * as INTERNAL_SERVER_ERROR) still flow through to Sentry.
 */
const EXPECTED_GRAPHQL_ERROR_CODES = new Set<string>([
  'BAD_USER_INPUT',
  'GRAPHQL_VALIDATION_FAILED',
  'GRAPHQL_PARSE_FAILED',
  'PERSISTED_QUERY_NOT_FOUND',
  'PERSISTED_QUERY_NOT_SUPPORTED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'QUOTE_EXPIRED'
]);

const isExpectedGraphQLError = (error: GraphQLErrorLike): boolean => {
  const code = error.extensions?.code;
  return typeof code === 'string' && EXPECTED_GRAPHQL_ERROR_CODES.has(code);
};

/**
 * Builds the Apollo error link responsible for forwarding *unexpected* GraphQL
 * and network errors to Sentry.
 *
 * Previously this logic was duplicated verbatim in `apolloClient.ts` and
 * `apolloLinks.ts`, reported every error (including expected business errors)
 * under a single generic `'A graphql error occurred'` message — which collapsed
 * thousands of unrelated issues into one and drowned production in noise. This
 * single implementation now:
 *   - skips known/expected GraphQL error codes,
 *   - reports a descriptive, code-based message (no raw error text in the
 *     title, so internal paths / PII are not leaked into the issue name),
 *   - sets a stable fingerprint so issues group by operation + code instead of
 *     all merging together,
 *   - attaches the full error payload as scoped `extra` for debugging.
 */
export function createErrorLink(): ApolloLink {
  const { REACT_APP_STAGE: stage } = getEnv();

  return onError(({ graphQLErrors, networkError, operation }) => {
    // Local development never reports — keeps dev consoles out of Sentry.
    if (stage === 'local') return;

    const operationName = operation?.operationName ?? 'unknown';

    if (graphQLErrors && graphQLErrors.length > 0) {
      const reportableErrors = graphQLErrors.filter(
        (error) => !isExpectedGraphQLError(error)
      );

      if (reportableErrors.length > 0) {
        Sentry.withScope((scope) => {
          scope.setExtra('graphQLErrors', reportableErrors);
          scope.setExtra('operationName', operationName);
          // Group by operation + the set of error codes, not by a single
          // shared message string.
          scope.setFingerprint([
            'graphql-error',
            operationName,
            ...reportableErrors.map(
              (error) =>
                (error.extensions?.code as string | undefined) ?? 'UNKNOWN'
            )
          ]);
          const code =
            (reportableErrors[0].extensions?.code as string | undefined) ??
            'UNKNOWN';
          Sentry.captureException(
            new Error(`GraphQL error [${code}] in operation "${operationName}"`)
          );
        });
      }
    }

    if (networkError) {
      Sentry.withScope((scope) => {
        scope.setExtra('networkError', networkError);
        scope.setExtra('operationName', operationName);
        scope.setFingerprint(['network-error', operationName]);
        Sentry.captureException(
          new Error(`Network error in operation "${operationName}"`)
        );
      });
    }
  });
}
